import { Button, Card, Divider, Icon } from '@nike/eds';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLoaderData, useRevalidator } from 'react-router-dom';

import Loader from '../../components/Loader';
import { QUERY_KEYS } from '../../constants';
import opensearchIndexesClient from '../../clientAPI/opensearchIndexes';
import propagate403Error from '../../utils/propagate403Error';

export default function OpenSearchIndexList() {
  const loaderData = useLoaderData() as { access_token: string };
  const revalidator = useRevalidator();
  const queryClient = useQueryClient();

  const {
    status,
    data: response,
    error,
  } = useQuery({
    queryKey: [QUERY_KEYS.OPENSEARCH_INDEX],
    queryFn: async () => {
      return opensearchIndexesClient.getAllIndexes();
    },
    staleTime: 60000, // Cache for 1 minute
    onError: (err: { status: number }) =>
      err.status === 401 && revalidator.revalidate(),
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries([QUERY_KEYS.OPENSEARCH_INDEX]);
  };

  propagate403Error(error as keyable);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const parseIndexData = (
    indexResponse: OpenSearchIndexResponse,
  ): OpenSearchIndex | null => {
    try {
      if (indexResponse.statusCode === 200) {
        return JSON.parse(indexResponse.body);
      }
      return null;
    } catch (e) {
      console.error('Failed to parse index data:', e);
      return null;
    }
  };

  return (
    <div className='tw-w-10/12 tw-mx-auto tw-py-10'>
      <div className='tw-flex tw-justify-between tw-items-center tw-mb-6'>
        <div>
          <p className='eds-type--display-4'>OpenSearch Indexes</p>
          {response && (
            <span className='tw-text-sm tw-text-gray-600 tw-mt-1'>
              Total: <strong>{response.count}</strong> index
              {response.count !== 1 ? 'es' : ''}
            </span>
          )}
        </div>
        <Button
          beforeSlot={<Icon name='Refresh' />}
          size='medium'
          variant='secondary'
          onClick={handleRefresh}
          disabled={status === 'loading'}
        >
          Refresh
        </Button>
      </div>

      {status === 'loading' ? (
        <Loader />
      ) : !response || response.indexes.length === 0 ? (
        <div className='tw-text-center tw-py-20'>
          <p className='eds-type--display-6 tw-text-gray-500'>
            No indexes found
          </p>
        </div>
      ) : (
        <div className='tw-space-y-6'>
          {response.indexes.map((indexResponse) => {
            const indexData = parseIndexData(indexResponse);

            if (!indexData) {
              return (
                <Card
                  key={indexResponse.indexId}
                  className='tw-p-6 tw-border-red-300'
                >
                  <div className='tw-text-red-600'>
                    <p className='eds-type--title-3'>
                      Error loading: {indexResponse.indexId}
                    </p>
                    <p className='tw-text-sm tw-mt-2'>
                      Status Code: {indexResponse.statusCode}
                    </p>
                  </div>
                </Card>
              );
            }

            return (
              <Card key={indexData.id} className='tw-p-6'>
                <div className='tw-mb-6'>
                  <div className='tw-flex tw-items-center tw-gap-4 tw-mb-4'>
                    <h2 className='eds-type--display-5'>{indexData.id}</h2>
                    <span className='tw-px-3 tw-py-1 tw-bg-blue-100 tw-text-blue-800 tw-rounded-full tw-text-sm tw-font-medium'>
                      {indexData.version}
                    </span>
                  </div>
                  <p className='tw-text-gray-600 tw-mb-2'>
                    {indexData.description}
                  </p>
                  <div className='tw-flex tw-gap-6 tw-text-sm tw-text-gray-500'>
                    <span>
                      Domain: <strong>{indexData.domain}</strong>
                    </span>
                    <span>
                      Resource: <strong>{indexData.resource}</strong>
                    </span>
                  </div>
                </div>

                <Divider />

                {indexData.languages?.length ? (
                  <>
                    <div className='tw-my-6'>
                      <h3 className='eds-type--title-3 tw-mb-4'>Languages</h3>
                      <div className='tw-flex tw-gap-2'>
                        {indexData.languages.map((lang) => (
                          <span
                            key={lang}
                            className='tw-px-3 tw-py-1 tw-bg-gray-200 tw-text-gray-800 tw-rounded-full tw-text-sm'
                          >
                            {lang}
                          </span>
                        ))}
                      </div>
                    </div>

                    <Divider />
                  </>
                ) : null}

                <div className='tw-my-6'>
                  <h3 className='eds-type--title-3 tw-mb-4'>Generations</h3>
                  <div className='tw-space-y-4'>
                    {indexData.generations.map((gen) => (
                      <Card key={gen.id} className='tw-p-4 tw-bg-gray-50'>
                        <div className='tw-flex tw-items-start tw-justify-between'>
                          <div className='tw-flex-1'>
                            <div className='tw-flex tw-items-center tw-gap-3 tw-mb-2'>
                              <span className='eds-type--title-4'>
                                Generation {gen.id}
                              </span>
                              <span
                                className={`tw-px-3 tw-py-1 tw-rounded-full tw-text-sm tw-font-medium ${
                                  gen.active
                                    ? 'tw-bg-green-100 tw-text-green-800'
                                    : 'tw-bg-gray-200 tw-text-gray-600'
                                }`}
                              >
                                {gen.active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <div className='tw-space-y-1 tw-text-sm'>
                              <p>
                                <span className='tw-text-gray-600'>
                                  Cluster ID:
                                </span>{' '}
                                <span className='tw-font-mono tw-text-xs'>
                                  {gen.clusterId}
                                </span>
                              </p>
                              <p>
                                <span className='tw-text-gray-600'>
                                  Read Traffic:
                                </span>{' '}
                                <strong>{gen.readTraffic}%</strong>
                              </p>
                              <p className='tw-break-all'>
                                <span className='tw-text-gray-600'>
                                  Endpoint:
                                </span>{' '}
                                <a
                                  href={gen.endpoint}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  className='tw-text-blue-600 hover:tw-underline tw-font-mono tw-text-xs'
                                >
                                  {gen.endpoint}
                                </a>
                              </p>
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>

                <Divider />

                <div className='tw-my-6'>
                  <h3 className='eds-type--title-3 tw-mb-4'>Configuration</h3>
                  <div className='tw-space-y-2'>
                    <div>
                      <span className='tw-text-gray-600 tw-text-sm'>
                        Schema URL:
                      </span>
                      <br />
                      <a
                        href={indexData.schemaUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='tw-text-blue-600 hover:tw-underline tw-text-sm tw-break-all'
                      >
                        {indexData.schemaUrl}
                      </a>
                    </div>
                    <div>
                      <span className='tw-text-gray-600 tw-text-sm'>
                        Setting URL:
                      </span>
                      <br />
                      <a
                        href={indexData.settingUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='tw-text-blue-600 hover:tw-underline tw-text-sm tw-break-all'
                      >
                        {indexData.settingUrl}
                      </a>
                    </div>
                  </div>
                </div>

                <Divider />

                <div className='tw-mt-6 tw-flex tw-gap-6 tw-text-sm tw-text-gray-500'>
                  <div>
                    <span>Created:</span>{' '}
                    <strong>{formatDate(indexData.creationDate)}</strong>
                  </div>
                  <div>
                    <span>Modified:</span>{' '}
                    <strong>{formatDate(indexData.modificationDate)}</strong>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
